import React, { useEffect, useState } from 'react';

const Modal = ({ isOpen, onClose, title, children, size = 'md', zIndex = 'z-50', headerActions }) => {
  const [showModal, setShowModal] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowModal(true);
      setAnimating(true);
      // Small delay to trigger animation
      setTimeout(() => setAnimating(false), 10);

      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else if (showModal) {
      setAnimating(true);
      // Wait for animation to complete before hiding
      setTimeout(() => {
        setShowModal(false);
        setAnimating(false);
        // Restore body scroll
        document.body.style.overflow = 'unset';
      }, 300);
    }

    // Handle escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      // Cleanup: restore body scroll on unmount
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showModal, onClose]);

  if (!showModal) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl'
  };

  return (
    <div
      className={`fixed inset-0 bg-gray-600 overflow-y-auto h-full w-full ${zIndex} transition-all duration-300 ease-in-out flex items-start justify-center p-4 ${
        animating && !isOpen ? 'bg-opacity-0' : 'bg-opacity-50'
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`relative p-4 border shadow-2xl rounded-lg bg-white transition-all duration-300 ease-in-out transform w-full ${
          animating && !isOpen
            ? 'scale-95 opacity-0 translate-y-4'
            : 'scale-100 opacity-100 translate-y-0'
        }`}
        style={{
          maxWidth: sizeClasses[size] || sizeClasses.md,
          maxHeight: '90vh'
        }}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h3 className="text-xl font-bold text-primary truncate">{title}</h3>
          <div className="flex items-center space-x-2">
            {headerActions && (
              <div className="flex items-center space-x-2">
                {headerActions}
              </div>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl font-light hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition-colors duration-200"
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>
        <div
          className="overflow-y-auto pr-2"
          style={{
            maxHeight: 'calc(90vh - 100px)' // Account for header and padding
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
